import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Type, Smile, X, Plus, Trash2, Palette, Link as LinkIcon, ChevronUp, Sparkles,
} from "lucide-react";
import AiBgGenerator from "./AiBgGenerator";

// ---------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------
export const STAGE_BACKGROUNDS = [
  { id: "none",     label: "Off",     gradient: null },
  { id: "neon-pop", label: "Neon",    gradient: "linear-gradient(135deg,#FF007F 0%,#D4FF00 50%,#00F0FF 100%)" },
  { id: "sunset",   label: "Sunset",  gradient: "linear-gradient(135deg,#FF512F 0%,#F09819 100%)" },
  { id: "candy",    label: "Candy",   gradient: "linear-gradient(135deg,#FA709A 0%,#FEE140 100%)" },
  { id: "ocean",    label: "Ocean",   gradient: "linear-gradient(135deg,#2E3192 0%,#1BFFFF 100%)" },
  { id: "violet",   label: "Violet",  gradient: "linear-gradient(135deg,#7F00FF 0%,#E100FF 100%)" },
  { id: "mint",     label: "Mint",    gradient: "linear-gradient(135deg,#11998E 0%,#38EF7D 100%)" },
  { id: "midnight", label: "Midnight",gradient: "linear-gradient(135deg,#0F2027 0%,#2C5364 100%)" },
  { id: "rose",     label: "Rose",    gradient: "linear-gradient(135deg,#F953C6 0%,#B91D73 100%)" },
  { id: "studio",   label: "Studio",  image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=900" },
  { id: "stage",    label: "Stage",   image: "https://images.unsplash.com/photo-1518609878373-06d740f60d8b?w=900" },
  { id: "neon-city",label: "City",    image: "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=900" },
  { id: "confetti", label: "Confetti",image: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=900" },
];

const TEXT_COLORS = ["#FAFAFA","#D4FF00","#FF007F","#00F0FF","#FFA500","#FFD700","#FF4D6D","#9B5DE5","#000000"];

const EMOJI_GROUPS = [
  { label: "Smileys", items: ["😀","😍","🥰","😎","🤩","😂","😘","🤗","🥹","🤪","🥳","🤤","😏","😜"] },
  { label: "Hearts",  items: ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","💖","💘","💝","💞","💕","💓"] },
  { label: "Hands",   items: ["👋","👌","✌️","🤞","🤟","🤘","👍","👎","🙌","👏","🙏","💪","🤝","🫶"] },
  { label: "Party",   items: ["🎉","🎊","🥂","🍾","🎁","🎈","🎂","🍰","✨","💫","⭐","🌟","🔥","💯"] },
  { label: "Shop",    items: ["🛍️","🛒","💸","💰","🏷️","💎","👑","💍","👗","👠","👜","👚","🎀","💄"] },
  { label: "Live",    items: ["📣","📢","🔔","💥","🚨","🌈","☀️","🌸","🌺","🦄","🐶","🐱","☕","🍕"] },
];

// ---------------------------------------------------------------------
// Hook — returns state + memoized Stage/Toolbar bound via context object
// ---------------------------------------------------------------------
export function useStudioStage() {
  const [bg, setBg] = useState(STAGE_BACKGROUNDS[0]);
  const [bgUrl, setBgUrl] = useState("");
  const [tab, setTab] = useState(null);
  const [overlays, setOverlays] = useState([]);
  const [selected, setSelected] = useState(null);
  const stageRef = useRef(null);

  const addText = (text, color = "#FAFAFA", size = 36) => {
    if (!text?.trim()) return;
    const id = "ov_" + Math.random().toString(36).slice(2, 9);
    setOverlays(o => [...o, { id, kind: "text", text: text.trim(), x: 50, y: 30, size, color }]);
    setSelected(id);
  };
  const addEmoji = (emoji, size = 64) => {
    const id = "ov_" + Math.random().toString(36).slice(2, 9);
    setOverlays(o => [...o, { id, kind: "emoji", emoji, x: 50, y: 50, size }]);
    setSelected(id);
  };
  const removeOverlay = (id) => setOverlays(o => o.filter(x => x.id !== id));
  const clearAll = () => { setOverlays([]); setSelected(null); };

  const captureThumbnail = (videoEl) => new Promise((resolve) => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 360; canvas.height = 640;
      const ctx = canvas.getContext("2d");
      const finishWithBg = () => drawVideoAndOverlays(ctx, canvas, videoEl, overlays).then(resolve);
      const useImageBg = bgUrl || bg?.image;
      if (useImageBg) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); finishWithBg(); };
        img.onerror = finishWithBg;
        img.src = useImageBg;
      } else if (bg?.gradient) {
        const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        grad.addColorStop(0, "#FF007F"); grad.addColorStop(0.5, "#D4FF00"); grad.addColorStop(1, "#00F0FF");
        ctx.fillStyle = grad; ctx.fillRect(0, 0, canvas.width, canvas.height); finishWithBg();
      } else {
        ctx.fillStyle = "#000"; ctx.fillRect(0, 0, canvas.width, canvas.height); finishWithBg();
      }
    } catch { resolve(null); }
  });

  // Memoized renderers as plain functions (not components) so React doesn't
  // remount on every parent render. We call them as JSX expressions.
  const Stage = useMemo(() => function StageRender(props) {
    return (
      <StageBox stageRef={stageRef} bg={bg} bgUrl={bgUrl}
        overlays={overlays} selected={selected}
        setSelected={setSelected} setOverlays={setOverlays}
        removeOverlay={removeOverlay}
        {...props}/>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bg, bgUrl, overlays, selected]);

  const Toolbar = useMemo(() => function ToolbarRender() {
    return (
      <ToolbarBox tab={tab} setTab={setTab}
        bg={bg} setBg={setBg} bgUrl={bgUrl} setBgUrl={setBgUrl}
        overlays={overlays} selected={selected} setSelected={setSelected}
        addText={addText} addEmoji={addEmoji} clearAll={clearAll}/>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, bg, bgUrl, overlays, selected]);

  return {
    Stage, Toolbar, captureThumbnail,
    bg, bgUrl, overlays,
    metadata: { bg_id: bg?.id, bg_url: bgUrl || bg?.image || null, overlays },
  };
}

// ---------------------------------------------------------------------
// Module-scope components (stable identity ⇒ no remount cascades)
// ---------------------------------------------------------------------
function StageBox({ stageRef, bg, bgUrl, overlays, selected, setSelected, setOverlays, removeOverlay,
                    videoNode, filterCss = "none", className = "", liveBadge = null }) {
  const bgStyle = useMemo(() => {
    if (bgUrl) return { backgroundImage: `url(${bgUrl})`, backgroundSize: "cover", backgroundPosition: "center" };
    if (bg?.image) return { backgroundImage: `url(${bg.image})`, backgroundSize: "cover", backgroundPosition: "center" };
    if (bg?.gradient) return { background: bg.gradient };
    return { background: "#000" };
  }, [bg, bgUrl]);

  const onStagePointerDown = (e) => {
    // only deselect when user clicks the empty stage, not a child
    if (e.target === e.currentTarget) setSelected(null);
  };

  const onOverlayPointerDown = (e, id) => {
    e.stopPropagation();
    const el = stageRef.current;
    if (!el) return;
    setSelected(id);
    const rect = el.getBoundingClientRect();
    const move = (ev) => {
      const cx = (ev.touches?.[0]?.clientX ?? ev.clientX) - rect.left;
      const cy = (ev.touches?.[0]?.clientY ?? ev.clientY) - rect.top;
      const x = Math.max(2, Math.min(98, (cx / rect.width) * 100));
      const y = Math.max(2, Math.min(98, (cy / rect.height) * 100));
      setOverlays(prev => prev.map(o => o.id === id ? { ...o, x, y } : o));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  // When a custom background is selected, contain the video inside the frame
  // so the chosen gradient/image is visible around it. With OFF, fill normally.
  const bgActive = !!bgUrl || !!bg?.image || !!bg?.gradient;
  const fitClass = bgActive ? "object-contain" : "object-cover";

  return (
    <div ref={stageRef}
      className={`relative mx-auto overflow-hidden rounded-xl ${className}`}
      style={{ ...bgStyle, aspectRatio: "9/16", width: "100%", maxWidth: "min(540px, 92vw)" }}
      onPointerDown={onStagePointerDown}
      data-testid="studio-stage"
    >
      <div className={`absolute inset-0 flex items-center justify-center pointer-events-none video-fit-${bgActive?"contain":"cover"}`} style={{ filter: filterCss }}>
        {videoNode}
      </div>
      {bg?.gradient && <div className="absolute inset-0 opacity-25 pointer-events-none mix-blend-overlay grain"/>}
      {liveBadge}
      {bgActive && (
        <div className="absolute top-2 left-2 z-10 bg-black/60 text-[#D4FF00] text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full pointer-events-none">
          BG · {bgUrl ? "Custom" : bg?.label}
        </div>
      )}
      {overlays.map(o => (
        <div key={o.id}
          onPointerDown={(e) => onOverlayPointerDown(e, o.id)}
          data-testid={`overlay-${o.id}`}
          className={`absolute -translate-x-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing select-none transition-transform ${selected === o.id ? "ring-2 ring-[#D4FF00] ring-offset-2 ring-offset-black/50" : ""}`}
          style={{
            left: `${o.x}%`, top: `${o.y}%`,
            fontSize: `${o.size}px`,
            color: o.color || undefined,
            textShadow: o.kind === "text" ? "0 2px 16px rgba(0,0,0,0.7)" : "none",
            fontWeight: o.kind === "text" ? 900 : "normal",
            lineHeight: 1, padding: "2px 6px",
            touchAction: "none",
          }}>
          {o.kind === "text" ? o.text : o.emoji}
        </div>
      ))}
      {selected && (
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            const id = selected;
            removeOverlay(id);
            setSelected(null);
          }}
          data-testid="overlay-delete-btn"
          aria-label="Delete overlay"
          className="absolute top-2 right-2 w-9 h-9 rounded-full bg-[#FF007F] text-white flex items-center justify-center z-10 active:scale-90">
          <Trash2 size={14}/>
        </button>
      )}
    </div>
  );
}

function ToolbarBox({ tab, setTab, bg, setBg, bgUrl, setBgUrl,
                      overlays, selected, setSelected, addText, addEmoji, clearAll }) {
  return (
    <div className="card p-3 mt-3" data-testid="studio-fx-toolbar">
      <div className="flex flex-wrap items-center gap-2">
        <FxBtn icon={Palette} label="Background" active={tab === "bg"}
          color="#D4FF00" onClick={() => setTab(tab === "bg" ? null : "bg")} testid="fx-bg-btn"/>
        <FxBtn icon={Type} label="Text" active={tab === "text"}
          color="#FF007F" onClick={() => setTab(tab === "text" ? null : "text")} testid="fx-text-btn"/>
        <FxBtn icon={Smile} label="Emoji" active={tab === "emoji"}
          color="#00F0FF" onClick={() => setTab(tab === "emoji" ? null : "emoji")} testid="fx-emoji-btn"/>
        {overlays.length > 0 && (
          <button onClick={clearAll} data-testid="fx-clear-btn"
            className="ml-auto text-xs px-3 py-1.5 rounded-full bg-[#3D2845] text-[#A1A1AA] hover:text-[#FF007F]">
            <X size={10} className="inline mr-1"/> Clear all ({overlays.length})
          </button>
        )}
        {tab && (
          <button onClick={() => setTab(null)} className="text-xs text-[#A1A1AA] hover:text-[#FAFAFA] ml-auto">
            <ChevronUp size={12} className="inline"/> Hide
          </button>
        )}
      </div>

      {tab === "bg" && <BgPicker bg={bg} setBg={setBg} bgUrl={bgUrl} setBgUrl={setBgUrl}/>}
      {tab === "text" && <TextPicker onAdd={addText}/>}
      {tab === "emoji" && <EmojiPicker onAdd={addEmoji}/>}

      {overlays.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#3D2845]">
          <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Layers · drag in preview to move</div>
          <div className="flex flex-wrap gap-2">
            {overlays.map(o => (
              <button key={o.id} onClick={() => setSelected(o.id)}
                data-testid={`fx-layer-${o.id}`}
                className={`chip text-xs ${selected === o.id ? "active" : ""}`}>
                {o.kind === "emoji" ? <span>{o.emoji}</span> : <span style={{color: o.color}}>{o.text}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FxBtn({ icon: Icon, label, active, color, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition border-2 ${
        active ? "border-[#D4FF00] text-black bg-[#D4FF00]"
               : "border-[#3D2845] hover:border-[#3D2845] text-[#FAFAFA]"
      }`}
      style={!active ? { borderColor: `${color}55` } : {}}>
      <Icon size={12} style={!active ? { color } : {}}/> {label}
    </button>
  );
}

function BgPicker({ bg, setBg, bgUrl, setBgUrl }) {
  const [draftUrl, setDraftUrl] = useState(bgUrl || "");
  const [mode, setMode] = useState("preset"); // preset | ai
  // Sync local draft if external bgUrl resets
  useEffect(() => { if (!bgUrl) setDraftUrl(""); }, [bgUrl]);
  return (
    <div className="mt-3 grid gap-3" data-testid="fx-bg-picker">
      <div className="flex gap-1" data-testid="fx-bg-mode-tabs">
        <button onClick={() => setMode("preset")}
          data-testid="fx-bg-mode-preset"
          className={`chip text-[10px] ${mode === "preset" ? "active" : ""}`}>
          Presets
        </button>
        <button onClick={() => setMode("ai")}
          data-testid="fx-bg-mode-ai"
          className={`chip text-[10px] ${mode === "ai" ? "active" : ""}`}>
          <Sparkles size={10} className="inline mr-1"/>AI
        </button>
      </div>

      {mode === "preset" && (
        <>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {STAGE_BACKGROUNDS.map(b => {
              const on = !bgUrl && bg?.id === b.id;
              return (
                <button key={b.id} onClick={() => { setBg(b); setBgUrl(""); setDraftUrl(""); }}
                  data-testid={`fx-bg-${b.id}`}
                  className={`shrink-0 w-20 text-center transition ${on ? "scale-105" : ""}`}>
                  <div className="aspect-square rounded-xl mb-1 border-2"
                    style={{
                      background: b.gradient || (b.image ? `url(${b.image}) center/cover` : "#3D2845"),
                      borderColor: on ? "#D4FF00" : "transparent",
                    }}/>
                  <div className="text-[10px] font-bold uppercase">{b.label}</div>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-[#3D2845]">
            <LinkIcon size={14} className="text-[#A1A1AA]"/>
            <input className="input-dark text-xs flex-1" placeholder="Paste an image URL…"
              value={draftUrl} onChange={e => setDraftUrl(e.target.value)}
              data-testid="fx-bg-url-input"/>
            <button onClick={() => setBgUrl(draftUrl)}
              disabled={!draftUrl} className="btn-primary text-xs px-3 py-1.5"
              data-testid="fx-bg-url-apply">Apply</button>
            {bgUrl && (
              <button onClick={() => { setBgUrl(""); setDraftUrl(""); }}
                className="text-xs text-[#FF007F]">Clear</button>
            )}
          </div>
        </>
      )}

      {mode === "ai" && (
        <AiBgGenerator
          onPick={(url) => { setBgUrl(url); setDraftUrl(url); }}
        />
      )}
    </div>
  );
}

function TextPicker({ onAdd }) {
  const [draft, setDraft] = useState("");
  const [color, setColor] = useState(TEXT_COLORS[1]);
  const [size, setSize] = useState(36);
  const submit = () => { onAdd(draft, color, size); setDraft(""); };
  return (
    <div className="mt-3 grid gap-2" data-testid="fx-text-picker">
      <div className="flex gap-2">
        <input className="input-dark text-sm flex-1" placeholder="Type text and hit Enter…"
          value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submit()}
          data-testid="fx-text-input"/>
        <button onClick={submit} disabled={!draft.trim()}
          className="btn-primary text-xs px-3 py-1.5" data-testid="fx-text-add">
          <Plus size={12} className="inline mr-1"/>Add
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] text-[#A1A1AA] uppercase font-bold">Color</span>
        {TEXT_COLORS.map(c => (
          <button key={c} onClick={() => setColor(c)}
            data-testid={`fx-text-color-${c.replace("#","")}`}
            aria-label={`color ${c}`}
            className={`w-6 h-6 rounded-full transition ${color === c ? "ring-2 ring-[#D4FF00] ring-offset-1 ring-offset-[#261534] scale-110" : ""}`}
            style={{ background: c }}/>
        ))}
        <span className="text-[10px] text-[#A1A1AA] uppercase font-bold ml-2">Size</span>
        {[24, 36, 56, 84].map(s => (
          <button key={s} onClick={() => setSize(s)}
            className={`chip text-xs ${size === s ? "active" : ""}`}>{s}px</button>
        ))}
      </div>
    </div>
  );
}

function EmojiPicker({ onAdd }) {
  return (
    <div className="mt-3 max-h-72 overflow-y-auto pr-1" data-testid="fx-emoji-picker">
      {EMOJI_GROUPS.map(g => (
        <div key={g.label} className="mb-3">
          <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">{g.label}</div>
          <div className="flex flex-wrap gap-1">
            {g.items.map(e => (
              <button key={e} onClick={() => onAdd(e)}
                data-testid={`fx-emoji-${e}`}
                className="w-9 h-9 rounded-lg hover:bg-[#3D2845] flex items-center justify-center text-xl active:scale-90">
                {e}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Canvas helper used by useStudioStage's captureThumbnail
async function drawVideoAndOverlays(ctx, canvas, videoEl, overlays) {
  if (videoEl && videoEl.videoWidth) {
    const ratio = Math.min(canvas.width / videoEl.videoWidth, canvas.height / videoEl.videoHeight);
    const w = videoEl.videoWidth * ratio;
    const h = videoEl.videoHeight * ratio;
    ctx.save();
    ctx.drawImage(videoEl, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
    ctx.restore();
  }
  for (const o of overlays) {
    const cx = canvas.width * (o.x / 100);
    const cy = canvas.height * (o.y / 100);
    ctx.save();
    ctx.font = `${o.kind === "text" ? "900 " : ""}${o.size}px ${o.kind === "text" ? "system-ui,Inter,Arial" : "system-ui"}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (o.kind === "text") {
      ctx.fillStyle = o.color || "#FFFFFF";
      ctx.shadowColor = "rgba(0,0,0,0.7)";
      ctx.shadowBlur = 8;
      ctx.fillText(o.text, cx, cy);
    } else {
      ctx.fillText(o.emoji, cx, cy);
    }
    ctx.restore();
  }
  return canvas.toDataURL("image/jpeg", 0.7);
}
